// v2 — design system: Plus Jakarta Sans, forest #0F3D2E, gold #E8B84C
// To rollback: swap import in App.tsx to ./pages/LandingPageV0
import { ArrowRight, CheckCircle2, Shield, BarChart3, Sparkles, Activity, Cloud, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CloudMark, Wordmark } from '../components/CloudFrugalLogo';
import Footer from '../components/Footer';

// Brand tokens
const FOREST = '#0F3D2E';
const GOLD   = '#E8B84C';
const DARK   = '#07281D';

// ─── Data ─────────────────────────────────────────────────────────────────────
const awsServices = [
  { name: 'EC2', label: 'Compute', icon: '/aws-icons/ec2.svg', stat: 'Up to 60% savings', description: 'Rightsize instances, schedule stop/start, surface Reserved Instance opportunities.' },
  { name: 'RDS', label: 'Databases', icon: '/aws-icons/rds.svg', stat: 'Up to 50% savings', description: 'Identify idle clusters, oversized instance classes, and deferrable multi-AZ configs.' },
  { name: 'S3', label: 'Storage', icon: '/aws-icons/s3.svg', stat: 'Up to 40% savings', description: 'Move cold objects to cheaper storage classes and detect forgotten buckets.' },
  { name: 'Lambda', label: 'Serverless', icon: '/aws-icons/lambda.svg', stat: 'Up to 35% savings', description: 'Tune memory allocation, spot over-provisioned timeouts, fix cold-start waste.' },
  { name: 'EBS', label: 'Block Storage', icon: '/aws-icons/ebs.svg', stat: 'Up to 45% savings', description: 'Find unattached volumes, oversized IOPS, and snapshot accumulation.' },
  { name: 'EKS', label: 'Kubernetes', icon: '/aws-icons/eks.svg', stat: 'Up to 55% savings', description: 'Surface underutilized node groups, idle pods, autoscaler blind spots.' },
];

const features = [
  { icon: Sparkles, title: 'AI Cost Intelligence', desc: 'Automated cost allocation and resource tagging across all your AWS accounts.', metric: '35–50% savings', color: FOREST, bg: '#e8f0ec' },
  { icon: BarChart3, title: 'EC2 & RDS Optimization', desc: 'Automated rightsizing, scheduling, and reserved instance recommendations.', metric: 'Up to 70% savings', color: '#1a6b42', bg: '#e8f0ec' },
  { icon: Activity, title: 'Anomaly Detection', desc: 'Catch unusual spending patterns and potential budget overruns before they hurt.', metric: 'Instant alerts', color: '#3d7f58', bg: '#e8f0ec' },
  { icon: Shield, title: 'Read-only & Secure', desc: 'We never touch your infrastructure. Read-only IAM, your data stays yours.', metric: '24/7 monitoring', color: '#1a6b3c', bg: '#E8F5E9' },
  { icon: Cloud, title: 'All Major Services', desc: 'EC2, RDS, S3, Lambda, EBS, EKS — everything in one cost dashboard.', metric: '30–60% savings', color: FOREST, bg: '#e8f0ec' },
  { icon: Heart, title: 'Honest Pricing', desc: 'Free to use. Pay only when we save you money — never before.', metric: 'No credit card', color: '#b71c1c', bg: '#FFEBEE' },
];

const steps = [
  { n: '01', title: 'Connect AWS', desc: 'Secure read-only IAM role. CloudFormation template — done in under 2 minutes.' },
  { n: '02', title: 'We Analyze', desc: 'Deep scan of your resources, usage patterns, and spend across every region.' },
  { n: '03', title: 'Get Insights', desc: 'Actionable recommendations: rightsizing, reserved instances, storage tiers.' },
  { n: '04', title: 'Track Savings', desc: 'See real dollar savings in the dashboard. Automate or act on your own terms.' },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Inter", system-ui, sans-serif' }}>

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section style={{ background: DARK }} className="relative overflow-hidden pt-28 pb-24 px-4">
        {/* subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: `linear-gradient(${GOLD} 1px,transparent 1px),linear-gradient(90deg,${GOLD} 1px,transparent 1px)`, backgroundSize: '48px 48px' }} />

        {/* lime glow top-right */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: GOLD }} />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          {/* badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8 border"
            style={{ background: 'rgba(232,184,76,0.1)', borderColor: 'rgba(232,184,76,0.3)', color: GOLD, fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ background: GOLD }} />
            Open Beta · Free to use
          </div>

          {/* hero logo lockup */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 mb-8">
            <CloudMark size={100} />
            <Wordmark size={56} variant="dark" />
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight"
            style={{ fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif', letterSpacing: '-0.02em' }}>
            Spend less on AWS.{' '}
            <span style={{ color: GOLD }}>Automatically.</span>
          </h1>

          <p className="text-lg mb-10 max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
            Continuous cost optimization for startups and engineering teams. Connect your account in 5 minutes and see savings by next week.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-12">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base transition-all hover:brightness-110 hover:-translate-y-0.5"
              style={{ background: GOLD, color: DARK, fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}
            >
              Get started free <ArrowRight className="w-4 h-4" />
            </button>
            <a href="#how-it-works"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base transition-all hover:bg-white/10"
              style={{ border: '1.5px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}
            >
              See how it works
            </a>
          </div>

          {/* trust pills */}
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { icon: <CheckCircle2 className="w-3.5 h-3.5" />, text: 'Read-only IAM access' },
              { icon: <Shield className="w-3.5 h-3.5" />, text: 'Your data stays yours' },
              { icon: <CheckCircle2 className="w-3.5 h-3.5" />, text: 'No credit card required' },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-1.5 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <span style={{ color: GOLD }}>{icon}</span>
                {text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AWS SERVICES ────────────────────────────────────────────────────── */}
      <section className="py-24 px-4" style={{ background: '#F6F1E3' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: FOREST, fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>
              AWS Services We Optimize
            </p>
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#161E2D', fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif', letterSpacing: '-0.02em' }}>
              Every major service, covered
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: 'rgba(15,22,32,0.55)' }}>
              One platform to monitor, analyze, and cut waste across your entire AWS footprint.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {awsServices.map((s) => (
              <div key={s.name}
                className="group bg-white rounded-2xl p-6 border transition-all hover:-translate-y-1 hover:shadow-xl cursor-default"
                style={{ borderColor: 'rgba(15,61,46,0.1)' }}>
                <div className="flex items-start justify-between mb-4">
                  <img src={s.icon} alt={s.name} className="w-12 h-12 object-contain" draggable={false} />
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                    style={{ background: '#e8f0ec', color: FOREST }}>
                    {s.stat}
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-0.5" style={{ color: '#161E2D', fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>
                  {s.name} <span className="text-sm font-medium" style={{ color: 'rgba(15,22,32,0.4)' }}>{s.label}</span>
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(15,22,32,0.55)' }}>{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ────────────────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: FOREST, fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>
              Features
            </p>
            <h2 className="text-4xl font-bold" style={{ color: '#161E2D', fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif', letterSpacing: '-0.02em' }}>
              Built for FinOps teams and solo founders alike
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-2xl p-7 border hover:shadow-lg transition-all" style={{ borderColor: 'rgba(15,61,46,0.1)' }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: f.bg }}>
                    <Icon className="w-5 h-5" style={{ color: f.color }} />
                  </div>
                  <h3 className="font-bold text-lg mb-2" style={{ color: '#161E2D', fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(15,22,32,0.55)' }}>{f.desc}</p>
                  <span className="text-sm font-semibold" style={{ color: FOREST }}>{f.metric}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 px-4" style={{ background: '#e8f0ec' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: FOREST, fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>
              How it works
            </p>
            <h2 className="text-4xl font-bold" style={{ color: '#161E2D', fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif', letterSpacing: '-0.02em' }}>
              Up and running in 5 minutes
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={s.n} className="relative bg-white rounded-2xl p-6 border" style={{ borderColor: 'rgba(15,61,46,0.08)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm mb-5 text-white"
                  style={{ background: FOREST, fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>
                  {s.n}
                </div>
                <h3 className="font-bold mb-2" style={{ color: '#161E2D', fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(15,22,32,0.55)' }}>{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-9 -right-3 w-6 h-px" style={{ background: FOREST, opacity: 0.2 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-14 px-4" style={{ background: FOREST }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight"
            style={{ fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif', letterSpacing: '-0.02em' }}>
            Ready to cut your AWS bill?
          </h2>
          <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Connect in 2 minutes. No credit card required.
          </p>
          <button onClick={() => navigate('/register')}
            className="inline-flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-base transition-all hover:brightness-110 hover:-translate-y-0.5"
            style={{ background: GOLD, color: DARK, fontFamily: '"Plus Jakarta Sans",system-ui,sans-serif' }}>
            Get started free <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <Footer />
    </div>
  );
}
